import axios, { AxiosInstance, AxiosError } from 'axios';
import type {
  MediFormClientConfig,
  FormDefinition,
  FormListItem,
  FormListFilter,
  PaginatedResponse,
  ApiResponse,
  SubmissionResult,
  FormData,
  MediFormError,
  ExtractionInput,
  ExtractionResult,
  Environment,
} from './types';

/**
 * MediForm Client SDK
 *
 * Provides methods to fetch forms from the MediForm API and submit form data.
 *
 * @example
 * ```typescript
 * const client = new MediFormClient({
 *   apiKey: 'mk_your_api_key_here',
 *   baseUrl: 'https://your-mediform-instance.com',
 *   cache: true,
 * });
 *
 * // List forms
 * const forms = await client.listForms({ category: 'intake' });
 *
 * // Get a specific form
 * const form = await client.getForm('form-id');
 *
 * // Render the form using FormRenderer component
 * <FormRenderer formDefinition={form.formDefinition} />
 * ```
 */
export class MediFormClient {
  private axios: AxiosInstance;
  private cache: Map<string, { data: any; expiry: number }>;
  private cacheEnabled: boolean;
  private cacheTimeout: number;
  private defaultEnvironment: string;

  constructor(config: MediFormClientConfig) {
    this.cacheEnabled = config.cache ?? true;
    this.cacheTimeout = config.cacheTimeout ?? 5 * 60 * 1000; // 5 minutes default
    this.defaultEnvironment = config.defaultEnvironment ?? 'prod';
    this.cache = new Map();

    // Create axios instance with base configuration
    this.axios = axios.create({
      baseURL: `${config.baseUrl}/api/v1/public`,
      headers: {
        'X-API-Key': config.apiKey,
        'Content-Type': 'application/json',
      },
      timeout: 30000, // 30 second timeout
    });

    // Add response interceptor for error handling
    this.axios.interceptors.response.use(
      (response) => response,
      (error: AxiosError<ApiResponse>) => {
        return Promise.reject(this.handleError(error));
      }
    );
  }

  /**
   * Handle API errors and convert to MediFormError
   */
  private handleError(error: AxiosError<ApiResponse>): Error {
    if (error.response?.data?.error) {
      const apiError = error.response.data.error;
      const MediFormError = class extends Error {
        constructor(
          message: string,
          public code: string,
          public statusCode?: number,
          public details?: any
        ) {
          super(message);
          this.name = 'MediFormError';
        }
      };

      return new MediFormError(
        apiError.message,
        apiError.code,
        error.response.status,
        apiError.details
      );
    }

    if (error.code === 'ECONNABORTED') {
      const TimeoutError = class extends Error {
        constructor(message: string, public code: string) {
          super(message);
          this.name = 'MediFormError';
        }
      };
      return new TimeoutError('Request timeout', 'TIMEOUT');
    }

    if (!error.response) {
      const NetworkError = class extends Error {
        constructor(message: string, public code: string) {
          super(message);
          this.name = 'MediFormError';
        }
      };
      return new NetworkError('Network error', 'NETWORK_ERROR');
    }

    return error;
  }

  /**
   * Get from cache if available and not expired
   */
  private getFromCache<T>(key: string): T | null {
    if (!this.cacheEnabled) return null;

    const cached = this.cache.get(key);
    if (!cached) return null;

    if (Date.now() > cached.expiry) {
      this.cache.delete(key);
      return null;
    }

    return cached.data as T;
  }

  /**
   * Store in cache
   */
  private setCache(key: string, data: any): void {
    if (!this.cacheEnabled) return;

    this.cache.set(key, {
      data,
      expiry: Date.now() + this.cacheTimeout,
    });
  }

  /**
   * List all available forms
   *
   * @param filter - Optional filters for category, tags, search, pagination
   * @returns Paginated list of forms
   *
   * @example
   * ```typescript
   * const { items, pagination } = await client.listForms({
   *   category: 'intake',
   *   page: 1,
   *   limit: 20,
   * });
   * ```
   */
  async listForms(filter?: FormListFilter): Promise<PaginatedResponse<FormListItem>> {
    const params = new URLSearchParams();

    if (filter?.page) params.append('page', filter.page.toString());
    if (filter?.limit) params.append('limit', filter.limit.toString());
    if (filter?.category) params.append('category', filter.category);
    if (filter?.search) params.append('search', filter.search);
    if (filter?.tags) params.append('tags', filter.tags.join(','));
    if (filter?.environment) {
      params.append('environment', filter.environment);
    } else {
      // Use default environment if not specified
      params.append('environment', this.defaultEnvironment);
    }

    const cacheKey = `list:${params.toString()}`;
    const cached = this.getFromCache<PaginatedResponse<FormListItem>>(cacheKey);
    if (cached) return cached;

    const response = await this.axios.get<ApiResponse<PaginatedResponse<FormListItem>>>(
      `/forms?${params.toString()}`
    );

    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to list forms');
    }

    const result = response.data.data;
    this.setCache(cacheKey, result);

    return result;
  }

  /**
   * Get a specific form by ID or template ID + environment
   *
   * @param formId - The published form ID or template ID
   * @param environment - Optional environment slug (defaults to client's defaultEnvironment)
   * @returns Complete form definition with formDefinition and customCss
   *
   * @example
   * ```typescript
   * // Get by published form ID
   * const form = await client.getForm('published-form-id');
   * 
   * // Get by template ID + environment
   * const devForm = await client.getForm('template-id', 'dev');
   * const prodForm = await client.getForm('template-id', 'prod');
   * ```
   */
  async getForm(formId: string, environment?: string): Promise<FormDefinition> {
    const env = environment || this.defaultEnvironment;
    const cacheKey = `form:${formId}:${env}`;
    const cached = this.getFromCache<FormDefinition>(cacheKey);
    if (cached) return cached;

    const params = new URLSearchParams();
    if (environment) {
      params.append('environment', environment);
    }

    const url = environment ? `/forms/${formId}?${params.toString()}` : `/forms/${formId}`;
    const response = await this.axios.get<ApiResponse<FormDefinition>>(url);

    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to fetch form');
    }

    const result = response.data.data;
    this.setCache(cacheKey, result);

    return result;
  }

  /**
   * Get a specific version of a form
   *
   * @param formId - The form template ID
   * @param version - The version number to fetch
   * @returns Complete form definition for the specified version
   *
   * @example
   * ```typescript
   * const form = await client.getFormVersion('template-id', 2);
   * ```
   */
  async getFormVersion(formId: string, version: number): Promise<FormDefinition> {
    const cacheKey = `form:${formId}:v${version}`;
    const cached = this.getFromCache<FormDefinition>(cacheKey);
    if (cached) return cached;

    const response = await this.axios.get<ApiResponse<FormDefinition>>(
      `/forms/${formId}/versions/${version}`
    );

    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to fetch form version');
    }

    const result = response.data.data;
    this.setCache(cacheKey, result);

    return result;
  }

  /**
   * Submit form data (if submission endpoint is enabled)
   *
   * @param formId - The form ID to submit to
   * @param data - The form data to submit
   * @returns Submission result with ID and status
   *
   * @example
   * ```typescript
   * const result = await client.submitForm('form-id', {
   *   patientName: 'John Doe',
   *   age: 45,
   *   symptoms: ['fever', 'cough'],
   * });
   *
   * console.log(result.id); // Submission ID
   * ```
   */
  async submitForm(formId: string, data: FormData): Promise<SubmissionResult> {
    const response = await this.axios.post<ApiResponse<SubmissionResult>>(
      `/forms/${formId}/submissions`,
      { data }
    );

    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to submit form');
    }

    return response.data.data;
  }

  /**
   * Clear all cached data
   *
   * @example
   * ```typescript
   * client.clearCache();
   * ```
   */
  clearCache(): void {
    this.cache.clear();
  }

  /**
   * Extract form values from an image or text using AI
   *
   * @param formId - The form ID to extract values for
   * @param input - The extraction input (image data URI or text)
   * @returns Extraction result with field values and confidence scores
   *
   * @example
   * ```typescript
   * // Extract from image
   * const result = await client.extractFormValues('form-id', {
   *   completedFormImageUri: 'data:image/png;base64,...'
   * });
   *
   * // Extract from text
   * const result = await client.extractFormValues('form-id', {
   *   completedFormText: 'Patient name: John Doe, Age: 45...'
   * });
   *
   * // Process results
   * Object.entries(result.extractions).forEach(([fieldId, extraction]) => {
   *   console.log(`${fieldId}: ${extraction.value} (confidence: ${extraction.confidence})`);
   * });
   * ```
   */
  async extractFormValues(formId: string, input: ExtractionInput): Promise<ExtractionResult> {
    // Validate that at least one input is provided
    if (!input.completedFormImageUri && !input.completedFormText) {
      throw new Error('Either completedFormImageUri or completedFormText must be provided');
    }

    const response = await this.axios.post<ApiResponse<ExtractionResult>>(
      `/forms/${formId}/extract`,
      input
    );

    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to extract form values');
    }

    return response.data.data;
  }

  /**
   * List available environments for the authenticated organization
   *
   * @returns List of environments with their slugs, names, and metadata
   *
   * @example
   * ```typescript
   * const environments = await client.listEnvironments();
   * console.log(environments); // [{ slug: 'prod', name: 'Production', isDefault: true }, ...]
   * ```
   */
  async listEnvironments(): Promise<{ environments: Array<{ slug: string; name: string; description?: string; isDefault: boolean; color?: string }> }> {
    const cacheKey = 'environments';
    const cached = this.getFromCache<{ environments: Array<any> }>(cacheKey);
    if (cached) return cached;

    const response = await this.axios.get<ApiResponse<{ environments: Array<any> }>>('/environments');

    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to list environments');
    }

    const result = response.data.data;
    this.setCache(cacheKey, result);

    return result;
  }

  /**
   * Check API health
   *
   * @returns Health status object
   *
   * @example
   * ```typescript
   * const health = await client.checkHealth();
   * console.log(health.status); // 'healthy'
   * ```
   */
  async checkHealth(): Promise<{ status: string; version: string; timestamp: string }> {
    const response = await this.axios.get<{
      status: string;
      version: string;
      timestamp: string;
    }>('/health');

    return response.data;
  }
}
